(() => {
  const initialize = (scope) => {
    scope.querySelectorAll('[data-wvr-carousel]').forEach((carousel) => {
      if (carousel.dataset.ready) return;
      carousel.dataset.ready = 'true';
      const slides = [...carousel.querySelectorAll('[data-wvr-slide]')];
      const buttons = [...carousel.querySelectorAll('[data-wvr-tab]')];
      const playback = carousel.querySelector('[data-wvr-playback]');
      const position = carousel.querySelector('[data-wvr-position]');
      const positionValue = carousel.querySelector('[data-wvr-position-value]');
      if (!slides.length || !buttons.length || !playback) return;
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      const progress = buttons.map(button => button.querySelector('[data-wvr-progress]'));
      const events = new AbortController();
      const listen = (target, type, listener) => target?.addEventListener(type, listener, { signal: events.signal });
      const duration = 8000;
      let paused = motion.matches;
      let progressAnimation;
      let hovered = false;
      let focused = false;
      let inView = true;
      let activeIndex = 0;
      let gesture = null;
      const resetProgress = () => {
        progressAnimation?.cancel();
        progress.forEach(fill => { if (fill) fill.style.transform = 'scaleX(0)'; });
        const fill = progress[activeIndex];
        if (!fill) return;
        const animation = fill.animate([
          { transform: 'scaleX(0)' },
          { transform: 'scaleX(1)' }
        ], { duration, easing: 'linear', fill: 'forwards' });
        animation.pause();
        progressAnimation = animation;
        animation.finished.then(() => {
          if (progressAnimation !== animation || !running()) return;
          activate(activeIndex + 1);
          reconcile();
        }, () => {});
      };
      const activate = (index) => {
        activeIndex = (index + slides.length) % slides.length;
        resetProgress();
        index = activeIndex;
        slides.forEach((slide, slideIndex) => {
          const inactive = slideIndex !== index;
          slide.hidden = inactive;
          slide.inert = inactive;
          slide.setAttribute('aria-hidden', String(inactive));
        });
        buttons.forEach((button, buttonIndex) => button.setAttribute('aria-pressed', String(buttonIndex === index)));
        if (positionValue) positionValue.textContent = `${index + 1} / ${slides.length}`;
        const tabBounds = buttons[index].getBoundingClientRect();
        const tabRow = buttons[index].parentElement;
        const rowBounds = tabRow.getBoundingClientRect();
        if (tabBounds.left < rowBounds.left || tabBounds.right > rowBounds.right) {
          tabRow.scrollTo({ left: buttons[index].offsetLeft - tabRow.offsetLeft - 20, behavior: 'auto' });
        }
      };
      const running = () => !paused && !hovered && !focused && !gesture && !document.hidden && inView && carousel.isConnected && slides.length > 1;
      const reconcile = () => {
        position?.setAttribute('aria-live', running() ? 'off' : 'polite');
        if (running()) {
          if (progressAnimation?.playState === 'finished') activate(activeIndex + 1);
          progressAnimation?.play();
        } else {
          progressAnimation?.pause();
        }
      };
      const select = index => {
        paused = true;
        if ((index + slides.length) % slides.length !== activeIndex) activate(index);
        updatePlayback();
        reconcile();
      };
      buttons.forEach((button, index) => {
        listen(button, 'click', () => select(index));
        listen(button, 'keydown', (event) => {
          let nextIndex;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % buttons.length;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + buttons.length) % buttons.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = buttons.length - 1;
          if (nextIndex === undefined) return;
          event.preventDefault();
          select(nextIndex);
          buttons[nextIndex].focus();
        });
      });
      const updatePlayback = () => {
        const label = paused ? playback.dataset.play : playback.dataset.pause;
        playback.dataset.paused = String(paused);
        playback.setAttribute('aria-label', label);
        playback.title = label;
      };
      playback.hidden = false;
      listen(playback, 'click', () => { paused = !paused; focused = false; updatePlayback(); reconcile(); });
      listen(carousel, 'pointerdown', event => {
        if (event.pointerType !== 'touch' || !event.isPrimary || !matchMedia('(max-width:767px)').matches || !event.target.closest('.wvr-hero__image')) return;
        gesture = { pointerId:event.pointerId, x:event.clientX, y:event.clientY };
        carousel.setPointerCapture(event.pointerId);
        reconcile();
      });
      listen(carousel, 'pointerup', event => {
        if (!gesture || gesture.pointerId !== event.pointerId) return;
        const deltaX = event.clientX - gesture.x;
        const deltaY = event.clientY - gesture.y;
        gesture = null;
        if (Math.abs(deltaX) >= 50 && Math.abs(deltaX) > Math.abs(deltaY) * 1.3) select(activeIndex + (deltaX < 0 ? 1 : -1));
        else reconcile();
      });
      listen(carousel, 'pointercancel', event => {
        if (gesture?.pointerId !== event.pointerId) return;
        gesture = null;
        reconcile();
      });
      listen(carousel, 'pointerover', event => {
        if (event.pointerType !== 'mouse') return;
        const reading = Boolean(event.target.closest('.wvr-hero__copy a, .wvr-hero__actions button'));
        if (reading !== hovered) { hovered = reading; reconcile(); }
      });
      listen(carousel, 'pointerleave', () => { hovered = false; reconcile(); });
      listen(carousel, 'focusin', event => {
        focused = event.target !== playback && (event.target.matches(':focus-visible') || Boolean(event.target.closest('[data-wvr-slide]')));
        reconcile();
      });
      listen(carousel, 'focusout', event => {
        if (!carousel.contains(event.relatedTarget)) { focused = false; reconcile(); }
      });
      const motionChanged = () => {
        if (motion.matches) paused = true;
        updatePlayback();
        reconcile();
      };
      listen(motion, 'change', motionChanged);
      listen(document, 'visibilitychange', reconcile);
      const observer = new IntersectionObserver(entries => {
        inView = entries[0].isIntersecting && entries[0].intersectionRatio >= .1;
        reconcile();
      }, { threshold: [0, .1] });
      observer.observe(carousel);
      const unload = event => {
        if (!event.target.contains(carousel)) return;
        progressAnimation?.cancel();
        observer.disconnect();
        events.abort();
        delete carousel.dataset.ready;
      };
      listen(document, 'shopify:section:unload', unload);
      activate(0);
      updatePlayback();
      reconcile();
    });
    scope.querySelectorAll('[data-wvr-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.ready) return;
      gallery.dataset.ready = 'true';
      const track = gallery.querySelector('[data-wvr-products]');
      const cards = [...track.querySelectorAll('.wvr-product')];
      const controls = gallery.querySelector('.wvr-product-controls');
      const position = gallery.querySelector('[data-wvr-product-position]');
      const previous = gallery.querySelector('[data-wvr-scroll="-1"]');
      const next = gallery.querySelector('[data-wvr-scroll="1"]');
      const currentIndex = () => {
        const left = track.getBoundingClientRect().left;
        return cards.reduce((nearest, card, index) => Math.abs(card.getBoundingClientRect().left - left) < Math.abs(cards[nearest].getBoundingClientRect().left - left) ? index : nearest, 0);
      };
      const update = () => {
        const scrollable = track.scrollWidth > track.clientWidth + 2;
        controls.hidden = !scrollable;
        track.tabIndex = scrollable ? 0 : -1;
        previous.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
        position.textContent = `${next.disabled ? cards.length : currentIndex() + 1} / ${cards.length}`;
      };
      gallery.querySelectorAll('[data-wvr-scroll]').forEach((button) => {
        button.addEventListener('click', () => {
          const index = Math.max(0, Math.min(cards.length - 1, currentIndex() + Number(button.dataset.wvrScroll)));
          const offset = cards[index].getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
          track.scrollTo({ left: offset, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        });
      });
      track.addEventListener('scroll', update, { passive: true });
      const observer = new ResizeObserver(update);
      observer.observe(track);
      const unload = (event) => {
        if (!event.target.contains(gallery)) return;
        observer.disconnect();
        document.removeEventListener('shopify:section:unload', unload);
      };
      document.addEventListener('shopify:section:unload', unload);
      update();
    });
    scope.querySelectorAll('[data-wvr-finder]').forEach((finder) => {
      if (finder.dataset.ready) return;
      finder.dataset.ready = 'true';
      const choices = [...finder.querySelectorAll('details')];
      choices.forEach((choice) => choice.addEventListener('toggle', () => {
        if (choice.open) choices.forEach((other) => { if (other !== choice) other.open = false; });
      }));
    });
  };
  initialize(document);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-wvr-slide]');
    if (!slide) return;
    const carousel = slide.closest('[data-wvr-carousel]');
    const index = [...carousel.querySelectorAll('[data-wvr-slide]')].indexOf(slide);
    carousel.querySelectorAll('[data-wvr-tab]')[index]?.click();
  });
})();
